import { DEFAULT_ANTI_SPIDER, METRIC_NAME, METRIC_NAME_MONITOR_JS_LOAD_TIME, METRIC_NAME_REWRITE } from '@/constants/index.ts';
import GlobalVariables, { GLOBAL_VARIABLES } from './global.ts';

export const fetchWithRetries = async (url: string, options: RequestInit = {}, maxRetries: number = 1, backoff: number = 1000): Promise<Response | any> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fetch(url, options)
    } catch (error) {
      if (attempt === maxRetries - 1) throw error;
      await new Promise(r => setTimeout(r, backoff * Math.pow(2, attempt)))
    }
  }
}

export const rewriteFetch = (_headers: string[], _sdks: string[]) => {
  const remove_headers = _headers || []
  if (typeof window !== 'undefined') {
    const originalFetch = window.fetch;
    window.fetch = async function (input, init = {}) {
      // 步骤1: 解析请求，获取 URL、method、headers、body、search
      let url = input as string;
      let method = 'GET';
      let headers: Headers | any;
      let body: BodyInit | null | undefined;
      let pathname = '';
      let search = '';

      try {
        // 如果 input 是一个 Request 对象，而非字符串 URL，则需要解析
        if (input instanceof Request) {
          url = input.url;
          method = input.method;
          headers = new Headers(input.headers); // 收集原有 headers
          body = input.body; // 收集 body
        } else {
          url = input as string;
          method = (init?.method || 'GET').toUpperCase();
          headers = init?.headers ? new Headers(init.headers) : new Headers(); // 收集原有 headers
          body = init?.body; // 收集 body
        }

        // 解析 URL，提取 pathname 和 search
        try {
          const urlObj = new URL(url, location.origin);
          pathname = urlObj.pathname;
          search = urlObj.search;
        } catch {
          pathname = '';
          search = '';
        }

        // 此时已经获取完整的请求信息：
        // - url: 完整 URL
        // - method: HTTP 方法 (GET, POST, etc.)
        // - pathname: URL 路径
        // - search: URL 查询参数
        // - headers: 用户设置的所有 headers
        // - body: 请求体数据

        // 步骤2: 根据获取的数据设置自定义 headers
        const customHeaders: Record<string, string> = {};

        // 处理 anti-in header
        if (remove_headers.includes('anti_in')) {
          const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
          const antiIn = getAntiIn()
          if (antiIn && hit?.some(path => url.includes(path))) {
            customHeaders['anti-in'] = antiIn;
          }
        } else {
          const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
          const globalHit = GlobalVariables?.get(GLOBAL_VARIABLES.ANTI_HIT) || []
          const hits = Array.from(new Set([...hit, ...globalHit]))
          const antiIn = getAntiIn()
          if (antiIn && hits?.some(path => url.includes(path))) {
            customHeaders['anti-in'] = antiIn;
          }
        }

        // 处理站内请求的自定义 headers
        if (isInSite(url)) {
          let channel = GlobalVariables.get(GLOBAL_VARIABLES.CHANNEL)

          // 处理 armorToken
          if (channel == 0 || channel == 1) {
            // pc pwa
            const at = getArmorToken() || ''
            if (at) {
              customHeaders['armorToken'] = at;
            }
          } else {
            // h5
            const at = window?.appComParams?.armorToken || window?.appComParams?.armortoken || window?.appComParams?.ArmorToken || window._s_ArmorToken || ''
            if (at) {
              customHeaders['armorToken'] = at;
            }
          }

          // 处理 x-gw-auth 签名
          const url2 = `${pathname}${search}` || '';
          try {
            if (url2 && window?._SHEIN_CALC_SIGNATURE_) {
              customHeaders['x-gw-auth'] = await window._SHEIN_CALC_SIGNATURE_(url2);
            }
          } catch (error) {
            GlobalVariables.get(GLOBAL_VARIABLES.METRIC)?.({
              metric_name: METRIC_NAME,
              tags: { url: url2, type: 'fetchCalcSignatureError' },
              value: Date.now(),
              message: 'Failed to fetch calculate signature',
              params: error
            });
          }

          // 处理 smDeviceId
          if (channel == 0 || channel == 1) {
            // pc pwa
            const sm = getSmDeviceId() || '';
            if (sm) {
              customHeaders['smDeviceId'] = sm;
            }
          } else {
            // h5
            const sm = window?.appComParams?.SmDeviceId || window?.appComParams?.smDeviceId || window?.appComParams?.smdeviceid || window._s_SmDeviceId || ''
            if (sm) {
              customHeaders['smDeviceId'] = sm;
            }
          }

          // 步骤3: 将自定义 headers 设置到请求中
          Object.entries(customHeaders).forEach(([key, value]) => {
            if (value) {
              headers.set(key, value);
            }
          });
          if (_sdks?.includes('csrandom')) {
            // const hit_cs = DEFAULT_ANTI_SPIDER?.csrandom?.hit || []
            // const globalHitCS = GlobalVariables?.get(GLOBAL_VARIABLES.CS_HIT) || []
            // const hits_cs = Array.from(new Set([...hit_cs, ...globalHitCS]))
            // if (hits_cs?.some(path => url.includes(path))) {
            const v = window?.CSRandom?.rand(search.substring(1), body, headers) || 'sdk error'
            headers.set('x-cs-random', v);
            // }
          }
        }
      } catch (error) {
        GlobalVariables.get(GLOBAL_VARIABLES.METRIC)?.({
          metric_name: METRIC_NAME_REWRITE,
          tags: { type: 'fetchError' },
          value: Date.now(),
          message: 'Failed to rewrite fetch',
          params: error
        });
      }

      // 步骤4: 发起请求
      const options = {
        ...init,
        headers,
        method,
        body
      };

      const response = await originalFetch(url, options);
      return response;
    };
  }
}

export const rewriteXHR = (_headers: string[], _sdks: string[]) => {
  const remove_headers = _headers || []
  if (typeof window !== 'undefined') {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

    // 重写 open 方法 - 保存 method、url、search 信息
    XMLHttpRequest.prototype.open = function () {
      try {
        // 步骤1: 保存请求的基本信息
        this._method = arguments[0]; // HTTP 方法 (GET, POST, etc.)
        this._url = arguments[1]; // 完整 URL
        this._isAsync = arguments[2] !== false; // 是否异步请求

        // 解析 URL，提取 pathname 和 search
        try {
          const urlObj = new URL(this._url || '', location.origin);
          this._pathname = urlObj.pathname;
          this._search = urlObj.search;
        } catch {
          this._pathname = '';
          this._search = '';
        }

        // 步骤2: 初始化 headers 收集对象
        this._userHeaders = {}; // 用于收集用户设置的 headers

        if (remove_headers.includes('anti_in')) {
          const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
          if (hit?.some(path => arguments[1].includes(path))) {
            this._customHeaders = {
              'anti-in': getAntiIn()
            };
          }
        } else {
          const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
          const globalHit = GlobalVariables?.get(GLOBAL_VARIABLES.ANTI_HIT) || []
          const hits = Array.from(new Set([...hit, ...globalHit]))
          if (hits?.some(path => arguments[1].includes(path))) {
            this._customHeaders = {
              'anti-in': getAntiIn()
            };
          }
        }
        if (isInSite(arguments[1])) {
          let channel = GlobalVariables.get(GLOBAL_VARIABLES.CHANNEL)
          if (channel == 0 || channel == 1) {
            // pc pwa
            const at = getArmorToken() || ''
            if (at) {
              this._customHeaders = {
                ...this._customHeaders,
                'armorToken': at,
              };
            }
          } else {
            // h5
            const at = window?.appComParams?.armorToken || window?.appComParams?.armortoken || window?.appComParams?.ArmorToken || window._s_ArmorToken || ''
            if (at) {
              this._customHeaders = {
                ...this._customHeaders,
                'armorToken': at,
              };
            }
          }
          let sigUrl = arguments[1]
          const url = getSigUrl(sigUrl)
          if (url) {
            this._customHeaders = {
              ...this._customHeaders,
              'x-gw-auth': url
            };

          }
          if (channel == 0 || channel == 1) {
            // pc pwa
            const sm = getSmDeviceId() || '';
            if (sm) {
              this._customHeaders = {
                ...this._customHeaders,
                'smDeviceId': sm
              };
            }
          } else {
            // h5
            const sm = window?.appComParams?.SmDeviceId || window?.appComParams?.smDeviceId || window?.appComParams?.smdeviceid || window._s_SmDeviceId || ''
            if (sm) {
              this._customHeaders = {
                ...this._customHeaders,
                'smDeviceId': sm
              };
            }
          }
        }
      } catch (error) {
        // 捕获所有异常，防止抛出导致请求失败
        GlobalVariables.get(GLOBAL_VARIABLES.METRIC)?.({
          metric_name: METRIC_NAME_REWRITE,
          tags: { type: 'xhrOpenError' },
          value: Date.now(),
          message: 'Failed to open XHR',
          params: error
        });
      }
      originalOpen.apply(this, arguments as any);
    };

    // 步骤2: 重写 setRequestHeader 方法 - 收集用户设置的 headers
    XMLHttpRequest.prototype.setRequestHeader = function (header: string, value: string) {
      try {
        // 收集用户设置的 headers
        if (!this._userHeaders) {
          this._userHeaders = {};
        }
        this._userHeaders[header] = value;
      } catch (error) {
        GlobalVariables.get(GLOBAL_VARIABLES.METRIC)?.({
          metric_name: METRIC_NAME_REWRITE,
          tags: { type: 'xhrSetHeaderError' },
          value: Date.now(),
          message: 'Failed to track setRequestHeader',
          params: error
        });
      }
      // 调用原始方法
      originalSetRequestHeader.call(this, header, value);
    };

    const originalSend = XMLHttpRequest.prototype.send;
    // 步骤3: 重写 send 方法 - 获取 body 和所有收集的数据
    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      try {
        // 保存请求 body
        this._requestBody = body;

        // 此时可以获取完整的请求信息：
        // this._method - HTTP 方法
        // this._url - 完整 URL
        // this._pathname - URL 路径
        // this._search - URL 查询参数
        // this._userHeaders - 用户设置的所有 headers
        // this._requestBody - 请求体数据
        // this._customHeaders - 自定义添加的 headers

        if (this?._customHeaders?.['anti-in']) {
          this.setRequestHeader('anti-in', this?._customHeaders?.['anti-in']);
        }
        if (this?._customHeaders?.['armorToken']) {
          this.setRequestHeader('armorToken', this?._customHeaders?.['armorToken']);
        }
        if (this?._customHeaders?.['smDeviceId']) {
          this.setRequestHeader('smDeviceId', this?._customHeaders?.['smDeviceId']);
        }
        // 处理 x-gw-auth 签名
        if (this?._customHeaders?.['x-gw-auth']) {
          const url = this._customHeaders['x-gw-auth'];
          if (this._isAsync) {
            // 异步请求：使用异步签名函数
            // oxlint-disable-next-line no-this-alias
            const self = this;
            const args = arguments;
            (async () => {
              const signature = await calcSignatureAsync(url);
              if (signature) {
                self.setRequestHeader('x-gw-auth', signature);
              }
              if (_sdks?.includes('csrandom') && isInSite(self?._url || '')) {
                // const hit_cs = DEFAULT_ANTI_SPIDER?.csrandom?.hit || []
                // const globalHitCS = GlobalVariables?.get(GLOBAL_VARIABLES.CS_HIT) || []
                // const hits_cs = Array.from(new Set([...hit_cs, ...globalHitCS]))
                // if (hits_cs?.some(path => self._url?.includes(path))) {
                const v = window?.CSRandom?.rand(self._search?.substring(1) || '', body, self._userHeaders) || 'sdk error'
                self.setRequestHeader('x-cs-random', v);
                // }
              }
              // 在签名计算完成后调用原始 send
              originalSend.apply(self, args as any);
            })();
            // 提前返回，避免重复调用 originalSend
            return;
          } else {
            // 同步请求：签名JS无同步方法 暂不支持
          }
        }
        if (_sdks?.includes('csrandom') && isInSite(this?._url || '')) {
          // const hit_cs = DEFAULT_ANTI_SPIDER?.csrandom?.hit || []
          // const globalHitCS = GlobalVariables?.get(GLOBAL_VARIABLES.CS_HIT) || []
          // const hits_cs = Array.from(new Set([...hit_cs, ...globalHitCS]))
          // if (hits_cs?.some(path => this._url?.includes(path))) {
          const v = window?.CSRandom?.rand(this._search?.substring(1) || '', body, this._userHeaders) || 'sdk error'
          this.setRequestHeader('x-cs-random', v);
          // }
        }
      } catch (err) {
        // 捕获所有异常，防止抛出导致请求失败
        GlobalVariables.get(GLOBAL_VARIABLES.METRIC)?.({
          metric_name: METRIC_NAME_REWRITE,
          tags: { type: 'xhrSendError' },
          value: Date.now(),
          message: 'Failed to send XHR',
          params: err
        });
      }
      // 同步请求或不需要签名的情况下，直接调用原始 send
      originalSend.apply(this, arguments as any);
    };
  }
}


// 计算签名 - 异步版本
export const calcSignatureAsync = async (url: string): Promise<string | null> => {
  try {
    if (!window._SHEIN_CALC_SIGNATURE_) {
      return null;
    }
    return await window._SHEIN_CALC_SIGNATURE_(url);
  } catch (error) {
    GlobalVariables.get(GLOBAL_VARIABLES.METRIC)?.({
      metric_name: METRIC_NAME,
      tags: { url, type: 'xhrCalcSignatureError' },
      value: Date.now(),
      message: 'Failed to xhr calculate signature',
      params: error
    });
    return null;
  }
}
// get anti-in
export const getAntiIn = () => {
  const AntiIn = window.AntiIn
  if (!AntiIn) return ''
  if (AntiIn._last) return AntiIn._last
  let anti_channel = 4; // h5
  let channel = GlobalVariables.get(GLOBAL_VARIABLES.CHANNEL);
  if (channel == 0 || channel == 1) {
    // pc pwa
    anti_channel = parseInt(channel);
  }
  AntiIn._calculating = AntiIn.syncGetAllEncrypted(anti_channel)
  AntiIn._last = AntiIn._calculating
  setTimeout(() => AntiIn._last = undefined, 200)
  return AntiIn._calculating
}

const isInSite = (url: string) => {
  if (!url) {
    return false
  }
  if (DEFAULT_ANTI_SPIDER?.anti_in?.hit?.some((path: string) => url?.includes(path))) {
    // 命中SDK路径
    return true
  }
  if (DEFAULT_ANTI_SPIDER?.csrandom?.hit?.some((path: string) => url?.includes(path))) {
    // 命中SDK路径
    return true
  }
  // if (/^http(s)?:\/\/www\.srmdata.com/.test(url)) {
  //   return true
  // }
  if (!/^http(s)?:\/\//.test(url)) { // 相对路径
    return true
  }
  const regExp = new RegExp(`^http(s)?://${location.host}`)
  if (regExp.test(url)) { // 本站地址
    return true
  }
  return false
}

const getArmorToken = () => {
  if (window.__ArmorToken) return window.__ArmorToken;
  window.__ArmorToken = window?.localStorage?.getItem('armorToken') || '';
  return window.__ArmorToken;
}

const getSmDeviceId = () => {
  if (window.__SmDeviceId) return window.__SmDeviceId;
  window.__SmDeviceId = window?.localStorage?.getItem('SmDeviceId') || '';
  return window.__SmDeviceId;
}

const getSigUrl = (url: string) => {
  // let sigUrl = url
  // let _query = sigUrl?.split('?')?.[1]?.replace(/'/g, '%27') || ''
  // if (_query) {
  //   sigUrl = sigUrl?.split('?')?.[0] + '?' + _query
  // } else {
  //   sigUrl = sigUrl?.split('?')?.[0]
  // }
  // return sigUrl
  const { pathname, search } = new URL(url, location.origin);
  return `${pathname}${search}` || '';
}

export const fetchJsonSafe = async (url: string, opts = {}) => {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const raw = await res.text().catch(() => '[unreadable body]');
    const err: any = new Error(`HTTP ${res.status} ${res.statusText}`);
    err.status = res.status;
    err.raw = raw;
    throw err;
  }

  // 检查 content-type 是否声明为 JSON
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (!ct.includes('application/json')) {
    const raw = await res.text().catch(() => '[unreadable body]');
    const err: any = new Error('Response is not application/json');
    err.raw = raw;
    err.contentType = ct;
    err.status = res.status;
    throw err;
  }

  try {
    const r = { response: await res.json(), status: res.status }
    return r;
  } catch (parseErr: any) {
    // res.json() 失败时，使用 clone() 读取原始文本
    const raw = await res.clone().text().catch(() => '[unreadable body]');
    const err: any = new Error('Invalid JSON response: ' + parseErr.message);
    err.original = parseErr;
    err.raw = raw;
    err.status = res.status;
    throw err;
  }
}

export const monitorSingleJsLoadTime = (keyword: string, assistName: string, assistKeyword?: string) => {
  if (!window.performance || !window.performance.getEntriesByType) return;
  const time = Date.now();
  const scriptEntries = window.performance.getEntriesByType('resource')
    .filter(entry => (entry as PerformanceResourceTiming).initiatorType === 'script');
  scriptEntries.forEach(entry => {
    const jsName = entry?.name?.split('/')?.slice(-1)?.[0] || '';
    if ((jsName.includes(keyword) && entry.name.includes(assistName))) {
      GlobalVariables.get(GLOBAL_VARIABLES.METRIC)?.({
        metric_name: METRIC_NAME_MONITOR_JS_LOAD_TIME,
        tags: { type: 'mjlt', url: entry.name, name: assistKeyword || jsName, duration: entry?.duration?.toFixed(0) },
        value: Date.now() - time,
        message: 'monitorJsLoadTime',
        params: JSON.parse(JSON.stringify(entry || {}))
      })
    }
  });
};
